// FI "Sleeve composition — current vs target by ballast role" (FixedIncomeView).
//
// Groups the Weights rows by the engine's own principal_role (etf_risk_profile, delivered as
// portfolio_risk.instrument_roles) — the same field as the Role column, so the two always agree.
//
// CIO 2026-10-23:
//   1. A role's net gap hides the trading inside it. Under the Calm floating mix Core Ballast
//      (VGSH · TFLO) nets +2.0% while the checklist sells ~$1.24M VGSH and buys ~$1.44M TFLO.
//      Every multi-ticker role now carries each ticker's own gap (`members`).
//   2. A role at 0% current / 0% target is not hidden: the engine uses Duration Workhorse, Credit
//      Leg and Inflation Ballast in its Defensive weight sets. It renders dimmed with a note.
//      The snapshot does not publish the engine's state weight sets, so the note is static:
//      in fixed_income_config.json VGIT / VCIT / STIP are 0 in neutral_weights (Calm) and
//      non-zero in both flight_to_quality_* (Defensive) sets.

/** Below this a weight counts as zero (0.05% of the sleeve) — the threshold the card always used. */
export const ZERO_WEIGHT = 0.0005;

export const ROLE_META: Record<string, { label: string; order: number }> = {
  crisis_cash: { label: "Crisis Cash", order: 1 },
  core_ballast: { label: "Core Ballast", order: 2 },
  calm_carry: { label: "Calm Carry", order: 3 },
  duration_workhorse: { label: "Duration Workhorse", order: 4 },
  ftq_octane: { label: "FTQ Octane", order: 5 },
  credit_leg: { label: "Credit Leg", order: 6 },
  inflation_ballast: { label: "Inflation Ballast", order: 7 },
};

export interface CompositionRow {
  etf: string;
  current: number;
  target: number;
  /** the Weights table's gap (meta.weight_diff, else target − current); per-ticker lines use it so they match the table */
  diff?: number;
  /** that gap in dollars; undefined when FI NAV is unavailable */
  diffDollar?: number;
}

export interface RoleMember {
  etf: string;
  cur: number;
  tgt: number;
  diff: number;
  diffDollar?: number;
}

export interface RoleComposition {
  key: string;
  label: string;
  order: number;
  cur: number;
  tgt: number;
  diff: number;
  diffDollar?: number;
  /** tickers shown beside the role name */
  etfs: string[];
  /** each ticker's own gap, in the same order as `etfs`; empty unless the role holds or targets 2+ tickers */
  members: RoleMember[];
  /** 0% current and 0% target (below ZERO_WEIGHT): kept on the card, dimmed, with `inactiveRoleNote` */
  inactive: boolean;
  /** inactive, but a residual below ZERO_WEIGHT is still held (the note must not say "not held") */
  residual: boolean;
}

const titleCase = (s: string) => s.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
// A rung is listed if it is held or targeted at all (> 0, as the card always did), so an off-roster
// residual stays visible even below ZERO_WEIGHT.
const live = (r: { current: number; target: number }) => r.target > 0 || r.current > 0;

export function sleeveComposition(rows: CompositionRow[], roleOf: (etf: string) => string): RoleComposition[] {
  const keys = Array.from(new Set(rows.map((r) => roleOf(r.etf))));
  return keys
    .map((key) => {
      const inCat = rows.filter((r) => roleOf(r.etf) === key);
      const known = ROLE_META[key];
      const tgt = inCat.reduce((a, r) => a + r.target, 0);
      const cur = inCat.reduce((a, r) => a + r.current, 0);
      const inactive = !(tgt > ZERO_WEIGHT || cur > ZERO_WEIGHT);
      // Roll the per-rung dollar gaps up to the role. If any rung is missing one (FI NAV
      // unavailable) the subtotal would understate the gap — leave it undefined ("—").
      const dollarParts = inCat.map((r) => r.diffDollar);
      const active = inCat.filter(live);
      // No per-ticker line on a dimmed role: a residual worth showing would make the role live.
      const members =
        !inactive && active.length >= 2
          ? active.map((r) => ({ etf: r.etf, cur: r.current, tgt: r.target, diff: r.diff ?? r.target - r.current, diffDollar: r.diffDollar }))
          : [];
      return {
        key,
        label: known ? known.label : key === "other" ? "Unclassified" : titleCase(key),
        order: known ? known.order : key === "other" ? 99 : 90,
        tgt,
        cur,
        diff: tgt - cur,
        diffDollar: dollarParts.some((d) => d == null) ? undefined : dollarParts.reduce((a: number, d) => a + (d as number), 0),
        // Live roles: rungs we hold or target (off-roster holdings stay visible). A 0/0 role
        // names every ticker it maps, so it is clear what is not being held.
        etfs: (inactive ? inCat : active).map((r) => r.etf),
        members,
        inactive,
        residual: inactive && cur > 0,
      };
    })
    // A 0/0 role stays only if it is one of the engine's roles; an unrecognised 0/0 bucket is noise.
    .filter((c) => !c.inactive || Boolean(ROLE_META[c.key]))
    .sort((a, b) => a.order - b.order || a.label.localeCompare(b.label));
}

/**
 * The note on a 0/0 role. `state` is the engine's detailed state code (Calm, Defensive-RISING, Re-arm, ...) and decides
 * which states to point at; `label` is how the page names that state elsewhere ("Defensive · Rising Rates"), used in
 * the text. `residual`: a tiny position is still held, so the note must not say "not held".
 */
export function inactiveRoleNote(
  state: string | null | undefined,
  label?: string | null,
  residual = false,
): string {
  const s = (state ?? "").trim();
  const name = (label ?? "").trim() || s || "the current state";
  const where = s.startsWith("Defensive") ? "used in other engine states" : "used in Defensive states";
  return `${residual ? "≈0% in" : "not held in"} ${name} · ${where}`;
}
